// DynamicSalesTable.js
import React, { useState, useRef } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableFooter, CircularProgress, Typography, Box
} from "@mui/material";

import { format } from "date-fns";

const formatNumber = (v) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const DynamicSalesTable = ({
    rows,
    grandColumnTotals,
    loading,
    loadNextPage,
    hasMore
}) => {

    const tableRef = useRef(null);

    const [collapsed, setCollapsed] = useState({});

    const toggle = (key) =>
        setCollapsed((p) => ({ ...p, [key]: !p[key] }));

    const handleScroll = () => {
        const target = tableRef.current;
        if (!target || loading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = target;
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };

    /** ----------------------------------------
     *  AUTO-GENERATE HEADERS FROM FIRST DETAIL ROW
     * ---------------------------------------- */
    const firstDetail = rows.find(
        (r) => !r.isGroupHeader && !r.isSubHeader && !r.isSubSubHeader
    );

    const dynamicColumns = firstDetail
        ? Object.keys(firstDetail).filter(
              (k) =>
                  ![
                      "isGroupHeader",
                      "isSubHeader",
                      "isSubSubHeader",
                      "DEPTNO",
                      "TYPE",
                      "CASHCRED",
                  ].includes(k)
          )
        : [];

    /** ----------------------------------------
     *  UTIL: DISPLAY ANY CELL VALUE SMARTLY
     * ---------------------------------------- */
    const renderCell = (key, value) => {
        if (!value) return "";

        if (key === "DATE") {
            return format(new Date(value), "dd-MM-yyyy");
        }

        if (typeof value === "number") {
            return formatNumber(value);
        }

        return value;
    };

    /** ----------------------------------------
     *  RENDER A ROW (Dynamic)
     * ---------------------------------------- */
    const renderRow = (row, index) => {
        const deptKey = row.DEPTNO?.trim();
        const typeKey = row.TYPE ? `${deptKey}-${row.TYPE.trim()}` : null;
        const cashKey = row.CASHCRED
            ? `${typeKey}-${row.CASHCRED.trim()}`
            : null;

        // hide when collapsed
        if (row.isSubHeader && collapsed[deptKey]) return null;
        if ((row.isSubSubHeader || row.CASHCRED) && collapsed[typeKey])
            return null;
        if (!row.isGroupHeader && !row.isSubHeader && !row.isSubSubHeader) {
            if (collapsed[cashKey]) return null;
        }

        // GROUP HEADER
        if (row.isGroupHeader) {
            return (
                <TableRow
                    key={`g-${index}`}
                    onClick={() => toggle(deptKey)}
                    sx={{ backgroundColor: "#e0f7fa", cursor: "pointer" }}
                >
                    <TableCell colSpan={dynamicColumns.length - 2}>
                        {collapsed[deptKey] ? "▶️" : "▼"} Department:{" "}
                        {row.DEPTNO}
                    </TableCell>
                    <TableCell align="right">
                        {formatNumber(row.totalAmountExclVAT)}
                    </TableCell>
                    <TableCell align="right">
                        {formatNumber(row.totalAmount)}
                    </TableCell>
                </TableRow>
            );
        }

        // SUB HEADER
        if (row.isSubHeader) {
            return (
                <TableRow
                    key={`s-${index}`}
                    onClick={() => toggle(typeKey)}
                    sx={{ backgroundColor: "#f0f4c3", cursor: "pointer" }}
                >
                    <TableCell colSpan={dynamicColumns.length - 2}>
                        {collapsed[typeKey] ? "▶️" : "▼"} Type: {row.TYPE}
                    </TableCell>
                    <TableCell align="right">
                        {formatNumber(row.totalAmountExclVAT)}
                    </TableCell>
                    <TableCell align="right">
                        {formatNumber(row.totalAmount)}
                    </TableCell>
                </TableRow>
            );
        }

        // SUB-SUB HEADER
        if (row.isSubSubHeader) {
            return (
                <TableRow
                    key={`ss-${index}`}
                    onClick={() => toggle(cashKey)}
                    sx={{ backgroundColor: "#fff8e1", cursor: "pointer" }}
                >
                    <TableCell colSpan={dynamicColumns.length - 2}>
                        {collapsed[cashKey] ? "▶️" : "▼"} CASH/CREDIT:{" "}
                        {row.CASHCRED}
                    </TableCell>
                    <TableCell align="right">
                        {formatNumber(row.totalAmountExclVAT)}
                    </TableCell>
                    <TableCell align="right">
                        {formatNumber(row.totalAmount)}
                    </TableCell>
                </TableRow>
            );
        }

        // DETAIL ROW (fully dynamic)
        return (
            <TableRow key={`d-${index}`}>
                {dynamicColumns.map((key) => (
                    <TableCell key={key}>
                        {renderCell(key, row[key])}
                    </TableCell>
                ))}
            </TableRow>
        );
    };

    return (
        <TableContainer
            ref={tableRef}
            onScroll={handleScroll}
            sx={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
        >
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        {dynamicColumns.map((col) => (
                            <TableCell key={col} sx={{ fontWeight: "bold" }}>
                                {col}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map(renderRow)}

                    {hasMore && (
                        <Box sx={{ textAlign: "center", py: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="caption">
                                Loading more…
                            </Typography>
                        </Box>
                    )}
                </TableBody>

                {Object.keys(grandColumnTotals).length > 0 && (
                    <TableFooter>
                        <TableRow
                            sx={{
                                backgroundColor: "#b3b3b3",
                                borderTop: "3px double #000",
                            }}
                        >
                            {dynamicColumns.map((key) => (
                                <TableCell
                                    key={key}
                                    align="right"
                                    sx={{
                                        fontWeight: "bold",
                                    }}
                                >
                                    {grandColumnTotals[key]
                                        ? formatNumber(grandColumnTotals[key])
                                        : ""}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableFooter>
                )}
            </Table>

            {loading && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        backgroundColor: "#ffffff80",
                    }}
                >
                    <CircularProgress size={48} />
                </div>
            )}
        </TableContainer>
    );
};

export default DynamicSalesTable;
